import { ReactNode, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Subscription } from "rxjs";
import { setCheckedPadScenarios } from "store/features";
import { RootState } from "store/rootReducer";
import { getSessionItem, setSessionItem } from "store/utils/sessionStorageHelper";

import { PadScenario } from "../../../../store/features/app/appSlice";
import { useProjectContext } from "../context";
import { useProjectInitialization } from "../hooks";

let projectSub: Subscription = null;

const STICK_SCENARIOS_SESSION_KEY = "stickScenariosPerProject";

export interface ProjectWrapperModel {
  children: ReactNode;
}

/**
 * Used to initialize the project context. With the way things are set up in the Home.tsx,
 * loading it here instead passing the ProjectProvider an initial value (essentially what's in the useEffect),
 * allows the layout to be unnaffected by the project context not being fully initialized.
 */
export default function ProjectWrapper({ children }: ProjectWrapperModel): JSX.Element {
  // state variables
  const [, setRefreshTxn] = useState<string>(null);

  // hooks
  const { projectRefreshObserver, selectedProjectId } = useProjectContext();
  const dispatch = useDispatch();
  const checkedPadScenarios = useSelector(
    (state: RootState) => state.app.checkedPadScenarios
  );
  useProjectInitialization();

  // Keep a ref to the latest scenarios so the project-switch effect can read them
  // synchronously without capturing a stale closure.
  const checkedPadScenariosRef = useRef<PadScenario[]>(checkedPadScenarios);
  checkedPadScenariosRef.current = checkedPadScenarios;

  // Keep a ref to selectedProjectId so the continuous save effect can read the
  // current project without listing it as a dependency (avoids the race where
  // selectedProjectId changes first, causing the save to write Project 1's
  // scenarios under Project 2's key before the switch effect restores).
  const selectedProjectIdRef = useRef<string | null>(selectedProjectId);
  selectedProjectIdRef.current = selectedProjectId;

  // Save checked scenarios to session storage whenever they change so that a
  // page refresh restores the same sticks for the current project.
  // Uses a ref for selectedProjectId — intentionally NOT in deps — to avoid
  // the race condition described above.
  useEffect(() => {
    const projectId = selectedProjectIdRef.current;
    if (!projectId) return;
    const map = getSessionItem<Record<string, PadScenario[]>>(
      STICK_SCENARIOS_SESSION_KEY,
      {}
    );
    map[projectId] = checkedPadScenarios;
    setSessionItem(STICK_SCENARIOS_SESSION_KEY, map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkedPadScenarios]);

  // When the active project changes:
  //   - Save the leaving project's scenarios to session storage.
  //   - Restore the new project's scenarios from session storage (or clear).
  // On first render (including after a page refresh), only restore — don't clear.
  const isFirstRender = useRef(true);
  const prevProjectIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (selectedProjectId) {
        const map = getSessionItem<Record<string, PadScenario[]>>(
          STICK_SCENARIOS_SESSION_KEY,
          {}
        );
        const saved = map[selectedProjectId] ?? [];
        if (saved.length > 0) {
          dispatch(setCheckedPadScenarios(saved));
        }
        prevProjectIdRef.current = selectedProjectId;
      }
      return;
    }

    // Save the previous project's scenarios before switching
    if (prevProjectIdRef.current) {
      const map = getSessionItem<Record<string, PadScenario[]>>(
        STICK_SCENARIOS_SESSION_KEY,
        {}
      );
      map[prevProjectIdRef.current] = checkedPadScenariosRef.current;
      setSessionItem(STICK_SCENARIOS_SESSION_KEY, map);
    }

    // Restore new project's scenarios (or clear if none saved)
    const map = getSessionItem<Record<string, PadScenario[]>>(
      STICK_SCENARIOS_SESSION_KEY,
      {}
    );
    const restored = map[selectedProjectId] ?? [];
    dispatch(setCheckedPadScenarios(restored));
    prevProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  // Monitor project refresh requests.
  useEffect(() => {
    if (projectRefreshObserver && !projectSub) {
      projectSub = projectRefreshObserver.subscribe((refreshTxn: string) => {
        setRefreshTxn(refreshTxn);
      });
    }
    return () => {
      if (projectSub) {
        projectSub.unsubscribe();
        projectSub = null;
      }
    };
  }, [projectRefreshObserver]);

  return <>{children}</>;
}
